import type { ThemeConfig } from 'antd';
import { colors, radius, typography } from './tokens';

/**
 * Ant Design 5.x ConfigProvider theme configuration
 * Maps Pitto design tokens to Ant Design's theme system
 */
export const antdTheme: ThemeConfig = {
  token: {
    // Colors
    colorPrimary: colors.primary,
    colorSuccess: colors.success,
    colorWarning: colors.warning,
    colorError: colors.error,
    colorInfo: colors.info,
    colorBgLayout: colors.bgPage,
    colorBgContainer: colors.bgCard,
    colorBorder: colors.border,
    colorBorderSecondary: colors.borderLight,
    colorText: colors.textPrimary,
    colorTextSecondary: colors.textSecondary,
    colorTextTertiary: colors.textTertiary,

    // Border Radius
    borderRadius: radius.md,
    borderRadiusLG: radius.lg,
    borderRadiusSM: radius.sm,

    // Typography
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSize.base,

    // Spacing
    marginXS: 8,
    marginSM: 12,
    margin: 16,
    marginMD: 20,
    marginLG: 24,
    marginXL: 32,

    // Misc
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    boxShadowSecondary: '0 4px 12px rgba(0,0,0,0.08)',
    controlHeight: 36,
    controlHeightSM: 28,
    controlHeightLG: 44,
  },

  components: {
    // Menu: clean, no heavy background
    Menu: {
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemSelectedBg: colors.bgPage,
      itemSelectedColor: colors.textPrimary,
      itemHoverBg: colors.bgPage,
      itemHoverColor: colors.textPrimary,
      itemActiveBg: colors.bgPage,
      iconSize: 16,
      itemBorderRadius: radius.sm,
    },

    // Card: border instead of shadow, larger radius
    Card: {
      borderRadiusLG: radius.lg,
      boxShadowTertiary: 'none',
      paddingLG: 24,
    },

    // Button: slightly rounded, clean hover
    Button: {
      borderRadius: radius.sm,
      borderRadiusLG: radius.md,
      borderRadiusSM: radius.sm,
      fontWeight: 500,
      controlHeight: 36,
      controlHeightSM: 28,
      controlHeightLG: 44,
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
    },

    // Table: clean headers, subtle borders
    Table: {
      headerBg: colors.bgPage,
      headerColor: colors.textSecondary,
      headerSplitColor: 'transparent',
      borderColor: colors.borderLight,
      rowHoverBg: '#FAFAFA',
      cellPaddingBlock: 14,
      cellPaddingInline: 24,
      headerBorderRadius: 0,
      fontSize: 13,
    },

    // Tag: rounded pill shape, softer colors
    Tag: {
      borderRadiusSM: radius.full,
      defaultBg: colors.bgPage,
      defaultColor: colors.textSecondary,
    },

    // Tabs: bottom border indicator (Vercel-style)
    Tabs: {
      inkBarColor: colors.textPrimary,
      itemSelectedColor: colors.textPrimary,
      itemHoverColor: colors.textPrimary,
      itemColor: colors.textSecondary,
      titleFontSize: 13,
      horizontalItemPadding: '12px 16px',
      horizontalMargin: '0',
    },

    // Input: clean, focused border
    Input: {
      borderRadius: radius.sm,
      activeBorderColor: colors.textPrimary,
      hoverBorderColor: '#ccc',
      activeShadow: `0 0 0 1px ${colors.textPrimary}`,
    },

    // Select
    Select: {
      borderRadius: radius.sm,
    },

    // Modal
    Modal: {
      borderRadiusLG: radius.lg,
      titleFontSize: 18,
    },

    // Statistic
    Statistic: {
      titleFontSize: 13,
      contentFontSize: 30,
    },

    // Dropdown
    Dropdown: {
      borderRadiusLG: radius.md,
      controlItemBgHover: colors.bgPage,
    },

    // Tooltip
    Tooltip: {
      borderRadius: radius.sm,
    },

    // Form
    Form: {
      labelFontSize: 13,
      verticalLabelPadding: '0 0 6px',
    },

    // Divider
    Divider: {
      colorSplit: colors.borderLight,
    },

    // Spin
    Spin: {
      colorPrimary: colors.primary,
    },

    // Breadcrumb
    Breadcrumb: {
      fontSize: 13,
      itemColor: colors.textTertiary,
      lastItemColor: colors.textPrimary,
      linkColor: colors.textTertiary,
      linkHoverColor: colors.textPrimary,
      separatorColor: colors.textTertiary,
    },

    // Skeleton
    Skeleton: {
      borderRadiusSM: radius.sm,
    },

    // Progress
    Progress: {
      defaultColor: colors.primary,
    },
  },
};
